import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { StationReportSummarySchema } from "@report/domain/models/report-summary.model.ts";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const ReportTracksStatusEnumSchema = z
    .enum(["PARADA", "DESPLAZAMIENTO"])
    .nullable();

export enum ReportGpsTypeEnum {
    dailyActivityGps = "daily_activity_gps",
    statesGps = "states_gps",
    detailedStatesGps = "detailed_states_gps",
    traveledKilometers = "traveled_kilometers",
    poisVisited = "pois_visited",
    poisRoutes = "pois_routes",
}

const ReportTracksSchema = z
    .object({
        timestamp: z.number(),
        status: ReportTracksStatusEnumSchema,
        position: z.string().trim(),
        latitude: z.string().trim(),
        longitude: z.string().trim(),
        values: z.record(z.string(), z.union([z.number(), z.string().trim()])),
    })
    .readonly();

export type ReportTracks = z.output<typeof ReportTracksSchema>;

const ReportRouteGpsSchema = z
    .object({
        roads: z.array(z.string().trim()),
        stationReportSummary: StationReportSummarySchema,
        tracks: z.array(ReportTracksSchema),
    })
    .readonly()
    .brand<"ReportRouteGps">();

export type ReportRouteGpsInputProps = z.input<typeof ReportRouteGpsSchema>;
export type ReportRouteGpsProps = z.output<typeof ReportRouteGpsSchema>;

export class ReportRouteGps {
    private constructor(
        private readonly props: Readonly<ReportRouteGpsProps>,
    ) {}

    get stationReportSummary(): Readonly<
        Unbrand<ReportRouteGpsProps>["stationReportSummary"]
    > {
        return this.props.stationReportSummary;
    }

    get roads(): string[] {
        return this.props.roads;
    }

    get tracks(): ReportTracks[] {
        return this.props.tracks;
    }

    static validate(
        props: Readonly<MaybeValidProps<ReportRouteGpsInputProps>>,
    ): Result<ReportRouteGpsProps, z.ZodError<ReportRouteGpsProps>> {
        const result = ReportRouteGpsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<ReportRouteGpsInputProps>>,
    ): Result<ReportRouteGpsProps, PartialValid<typeof ReportRouteGps>> {
        const result = partialSafeParse(ReportRouteGpsSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<ReportRouteGpsInputProps>,
    ): Result<ReportRouteGps, z.ZodError<ReportRouteGpsProps>> {
        return ReportRouteGps.validate(props).map(
            (validatedProps) => new ReportRouteGps(validatedProps),
        );
    }

    static createWithRecovery(
        props: ReportRouteGpsInputProps,
    ): Result<ReportRouteGps, ModelError<typeof ReportRouteGps>> {
        return ReportRouteGps.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.stationReportSummary?.stationId)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<ReportRouteGpsProps> = {
                    stationReportSummary: {
                        stationId: 0,
                        stationName: "",
                        icon: "",
                    },
                    roads: [],
                    tracks: [],
                };

                const recoveryProps: Unbrand<ReportRouteGpsProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof ReportRouteGps>(
                    new ReportRouteGps(recoveryProps as ReportRouteGpsProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new ReportRouteGps(validatedProps));
    }

    toCreateProps(): ReportRouteGpsInputProps {
        return {
            stationReportSummary: this.stationReportSummary,
            tracks: this.tracks,
            roads: this.props.roads,
        };
    }
}

export interface ReportRouteGpsFilter {
    from: number;
    to: number;
    stationId: number;
}
